import { useRollbar } from '@rollbar/react'
import { useState } from 'react'
import { usePopper } from 'react-popper'
import { useTranslation } from 'next-i18next'
import { Loader } from 'shared/components/loader'
import { useCommentLikes } from 'modules/comments/api/get-comment-likes'
import { LikeInterface } from 'modules/comments/api/types'
import { usePostLikes } from 'modules/community/api/get-post-likes'

interface LikeDetailPopoverProps {
  likesCount: number
  postId?: number
  commentId?: number
  referenceElement: HTMLElement | null
}

const MAX_DISPLAYED_LIKES = 7

export const LikeDetailPopover = ({
  likesCount,
  referenceElement,
  postId,
  commentId,
}: LikeDetailPopoverProps) => {
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const rollbar = useRollbar()
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    strategy: 'fixed',
  })

  const { data: postLikes, isLoading: isPostLoading } = usePostLikes({
    postId: postId!,
    enabled: !!postId,
  })
  const { data: commentLikes, isLoading: isCommentLoading } = useCommentLikes({
    commentId: commentId!,
    enabled: !!commentId,
  })

  if (!postId && !commentId) {
    rollbar.error('LikeDetailPopover: either postId or commentId must be provided')
    return null
  }

  const isLoading = isPostLoading || isCommentLoading
  const likes = postLikes || commentLikes || []

  const overflowLikes = likesCount > MAX_DISPLAYED_LIKES ? likesCount - MAX_DISPLAYED_LIKES : 0

  return (
    <div ref={setPopperElement} style={styles.popper} {...attributes.popper} className="z-10">
      <div className="pb-3">
        <div className="flex w-fit min-w-[120px] max-w-[180px] flex-col items-center rounded-md bg-background-secondary px-5 py-4 text-sm text-text-primary shadow-[0px_0px_20px_0px_#062A4033]">
          {isLoading ? (
            <Loader className="size-8 text-brand-primary" />
          ) : (
            <LikesDetailContent likes={likes} overflowLikes={overflowLikes} />
          )}
        </div>
      </div>
    </div>
  )
}

function LikesDetailContent({
  likes,
  overflowLikes,
}: {
  likes: LikeInterface[]
  overflowLikes: number
}) {
  const { t } = useTranslation()
  return (
    <>
      {likes?.slice(0, MAX_DISPLAYED_LIKES).map(like => (
        <p
          className="truncate text-xs [&:not(:first-child)]:pt-2.5"
          key={like.createdAt + like.displayName}
        >
          {like.displayName}
        </p>
      ))}
      {overflowLikes > 0 && (
        <p className="whitespace-nowrap pt-2.5 text-xs text-text-primary/60">
          {t('post.likes.popup.and_others', { count: overflowLikes })}
        </p>
      )}
    </>
  )
}
